/**
 * Frontend Telemetry Service (v3 — Full Capture)
 *
 * Captures EVERYTHING happening in the product:
 *   - JS errors, React crashes, unhandled rejections
 *   - API calls (BOTH success AND errors with full timing)
 *   - Page views, clicks, form submissions
 *   - Console errors from third-party libraries
 *   - Page performance (load times, navigation timing)
 *   - Session awareness (heartbeat, tab visibility, online/offline)
 *
 * Every event includes: session ID, breadcrumb trail, browser info,
 * full URLs, stack traces, request/response bodies, timing data.
 *
 * Batches events and sends to /api/telemetry/events every 10 seconds.
 */

const FLUSH_INTERVAL_MS = 10000;
const MAX_BATCH_SIZE = 50;
const MAX_BREADCRUMBS = 25;
const MAX_STRING = 2000; // max chars for large strings (stacks, bodies)
const HEARTBEAT_INTERVAL_MS = 60000; // 60s heartbeat
const SLOW_API_THRESHOLD_MS = 3000; // Flag API calls slower than 3s

let eventQueue = [];
let flushTimer = null;
let heartbeatTimer = null;
let isInitialized = false;

// ---- Session & Breadcrumbs ----

// Stable per-tab session ID (survives SPA navigations, resets on new tab)
const SESSION_ID = `s_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
let breadcrumbs = [];

function addBreadcrumb(type, summary) {
  breadcrumbs.push({
    type,
    summary: String(summary).substring(0, 120),
    ts: new Date().toISOString(),
  });
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs = breadcrumbs.slice(-MAX_BREADCRUMBS);
  }
}

// ---- Browser / Device Context (computed once) ----

const browserContext = (() => {
  const ua = navigator.userAgent || '';
  let browser = 'Unknown';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  return {
    userAgent: ua.substring(0, 300),
    browser,
    platform: navigator.platform || '',
    language: navigator.language || '',
    cookiesEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  };
})();

// ---- Helpers ----

function truncate(str, max = MAX_STRING) {
  if (!str) return '';
  const s = typeof str === 'string' ? str : JSON.stringify(str);
  return s.length > max ? s.substring(0, max) + `...[truncated ${s.length - max} chars]` : s;
}

function safeStringify(obj, max = MAX_STRING) {
  if (!obj) return '';
  try {
    const s = JSON.stringify(obj);
    return truncate(s, max);
  } catch {
    return '[unserializable]';
  }
}

function getCssSelector(el) {
  if (!el || !el.tagName) return '';
  const parts = [];
  let node = el;
  for (let i = 0; i < 5 && node && node.tagName; i++) {
    let sel = node.tagName.toLowerCase();
    if (node.id) { sel += `#${node.id}`; parts.unshift(sel); break; }
    if (node.className && typeof node.className === 'string') {
      const cls = node.className.trim().split(/\s+/).slice(0, 2).join('.');
      if (cls) sel += `.${cls}`;
    }
    parts.unshift(sel);
    node = node.parentElement;
  }
  return parts.join(' > ').substring(0, 200);
}

// Get user info from localStorage if available
function getUserInfo() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      user_id: user.email || null,
      user_email: user.email || null,
      user_name: user.name || null,
      user_role: user.role || null,
    };
  } catch {
    return { user_id: null, user_email: null, user_name: null, user_role: null };
  }
}

// Get the backend URL for telemetry (same as axiosInstance base)
function getBackendUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return base ? `${base}/api` : '/api';
}

// ---- Core: Queue + Flush ----

function trackEvent(type, details = {}, severity = 'info') {
  // Add breadcrumb for every event (errors get the trail attached)
  addBreadcrumb(type, details.message || details.label || details.path || details.url || type);

  const event = {
    type,
    timestamp: new Date().toISOString(),
    page: window.location.pathname + window.location.search,
    fullUrl: window.location.href,
    severity,
    sessionId: SESSION_ID,
    ...getUserInfo(),
    browser: browserContext,
    details,
  };

  // Attach breadcrumb trail to error events for context
  if (severity === 'error') {
    event.breadcrumbs = [...breadcrumbs.slice(-15)]; // last 15 actions before error
  }

  eventQueue.push(event);

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flush();
  }
}

function flush() {
  if (eventQueue.length === 0) return;

  const events = eventQueue.splice(0, MAX_BATCH_SIZE);
  const url = `${getBackendUrl()}/telemetry/events`;
  const payload = JSON.stringify({ events });

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const sent = navigator.sendBeacon(url, blob);
    if (!sent) fetchFallback(url, payload);
  } else {
    fetchFallback(url, payload);
  }
}

function fetchFallback(url, payload) {
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silently ignore telemetry send failures
  });
}

// ---- Capture Handlers ----

function handleWindowError(message, source, lineno, colno, error) {
  trackEvent('js_error', {
    message: String(message),
    source: source || '',
    lineno,
    colno,
    stack: truncate(error?.stack, MAX_STRING),
    errorName: error?.name || '',
    // What was the user doing?
    currentUrl: window.location.href,
    documentTitle: document.title,
  }, 'error');
}

function handleUnhandledRejection(event) {
  const reason = event.reason;
  const isAxiosError = reason?.isAxiosError || reason?.config;
  trackEvent('unhandled_rejection', {
    message: reason?.message || String(reason),
    stack: truncate(reason?.stack, MAX_STRING),
    errorName: reason?.name || '',
    errorCode: reason?.code || '',
    // If it's an axios error that escaped the interceptor, capture API details
    ...(isAxiosError ? {
      apiUrl: reason.config?.url || '',
      apiMethod: (reason.config?.method || '').toUpperCase(),
      apiStatus: reason.response?.status || 0,
      apiResponseBody: safeStringify(reason.response?.data, 500),
    } : {}),
  }, 'error');
}

function handleBeforeUnload() {
  flush();
}

function handleClick(event) {
  const target = event.target.closest('button, a, [role="button"], [data-track], input[type="submit"]');
  if (!target) return;

  const label =
    target.getAttribute('data-track') ||
    target.getAttribute('aria-label') ||
    target.textContent?.trim().substring(0, 80) ||
    target.tagName;

  trackEvent('click', {
    element: target.tagName.toLowerCase(),
    label,
    href: target.href || '',
    id: target.id || '',
    cssSelector: getCssSelector(target),
    disabled: target.disabled || false,
    dataAttributes: Object.fromEntries(
      Object.entries(target.dataset || {}).slice(0, 5)
    ),
  });
}

// Capture console.error calls (catches libraries that log errors)
function patchConsoleError() {
  const originalError = console.error;
  console.error = function (...args) {
    originalError.apply(console, args);
    // Don't track our own telemetry failures
    const msg = args.map(a => (typeof a === 'string' ? a : safeStringify(a, 300))).join(' ');
    if (msg.includes('/telemetry/')) return;
    trackEvent('console_error', {
      message: truncate(msg, 500),
      argCount: args.length,
    }, 'warning');
  };
}

// ---- NEW v3: API Success Tracking ----

/**
 * Track a SUCCESSFUL API call with timing data.
 * This fills the biggest gap: seeing slow-but-working endpoints.
 */
function trackApiSuccess(response) {
  const config = response.config || {};
  // Skip telemetry endpoint to avoid loops
  if (config.url?.includes('/telemetry/')) return;

  const duration = config._startTime ? Date.now() - config._startTime : null;
  const isSlow = duration && duration > SLOW_API_THRESHOLD_MS;

  trackEvent('api_call', {
    url: config.url || '',
    fullUrl: (config.baseURL || '') + (config.url || ''),
    method: (config.method || '').toUpperCase(),
    status: response.status,
    statusText: response.statusText || '',
    durationMs: duration,
    // Only include response size estimate (not full body for success)
    responseSize: safeStringify(response.data, 10)?.length > 10
      ? JSON.stringify(response.data)?.length || 0 : 0,
    slow: isSlow,
  }, isSlow ? 'warning' : 'info');
}

// ---- NEW v3: Form Submission Tracking ----

function handleFormSubmit(event) {
  const form = event.target;
  if (!form || form.tagName !== 'FORM') return;

  const formName =
    form.getAttribute('data-track') ||
    form.getAttribute('name') ||
    form.getAttribute('id') ||
    form.action?.split('/').pop() ||
    'unnamed_form';

  // Count input fields
  const inputs = form.querySelectorAll('input, select, textarea');
  const filledCount = Array.from(inputs).filter(
    (el) => el.value && el.value.trim() !== ''
  ).length;

  trackEvent('form_submit', {
    formName,
    fieldCount: inputs.length,
    filledFields: filledCount,
    action: form.action || '',
    method: form.method || 'get',
  });
}

// ---- NEW v3: Tab Visibility Tracking ----

function handleVisibilityChange() {
  const state = document.visibilityState; // 'visible' or 'hidden'
  trackEvent('visibility_change', {
    state,
    hiddenSince: state === 'visible' ? undefined : new Date().toISOString(),
  }, 'info');
}

// ---- NEW v3: Online/Offline Tracking ----

function handleOnline() {
  trackEvent('connectivity', { status: 'online' }, 'info');
}

function handleOffline() {
  trackEvent('connectivity', { status: 'offline' }, 'warning');
}

// ---- NEW v3: Session Heartbeat ----

function sendHeartbeat() {
  trackEvent('heartbeat', {
    uptime: Math.round((Date.now() - sessionStartTime) / 1000),
    queueSize: eventQueue.length,
    visible: document.visibilityState === 'visible',
    online: navigator.onLine,
    memoryUsage: performance?.memory?.usedJSHeapSize || null,
  }, 'info');
}

const sessionStartTime = Date.now();

// ---- NEW v3: Page Performance Tracking ----

function trackPagePerformance() {
  // Use PerformanceObserver for navigation timing (works in modern browsers)
  try {
    if (!window.PerformanceObserver) return;

    // Track long tasks (>50ms) that cause jank
    const longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 200) { // Only flag really long tasks (>200ms)
          trackEvent('long_task', {
            durationMs: Math.round(entry.duration),
            startTime: Math.round(entry.startTime),
            name: entry.name || 'self',
          }, 'warning');
        }
      }
    });
    longTaskObserver.observe({ type: 'longtask', buffered: true });

    // Track initial page load timing
    setTimeout(() => {
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      if (nav) {
        trackEvent('page_load', {
          dnsMs: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
          connectMs: Math.round(nav.connectEnd - nav.connectStart),
          ttfbMs: Math.round(nav.responseStart - nav.requestStart),
          downloadMs: Math.round(nav.responseEnd - nav.responseStart),
          domInteractiveMs: Math.round(nav.domInteractive - nav.startTime),
          domCompleteMs: Math.round(nav.domComplete - nav.startTime),
          loadEventMs: Math.round(nav.loadEventEnd - nav.startTime),
          transferSize: nav.transferSize || 0,
        });
      }
    }, 3000); // Wait for page to fully load
  } catch (e) {
    // PerformanceObserver not supported — skip silently
  }
}

// ---- Public API ----

function initTelemetry() {
  if (isInitialized) return;
  isInitialized = true;

  // Capture JS errors
  window.addEventListener('error', (e) => {
    handleWindowError(e.message, e.filename, e.lineno, e.colno, e.error);
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Flush on page unload
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Capture clicks on interactive elements
  document.addEventListener('click', handleClick, true);

  // v3: Capture form submissions
  document.addEventListener('submit', handleFormSubmit, true);

  // v3: Track tab visibility changes (user switching tabs)
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // v3: Track online/offline events
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Patch console.error for library errors
  patchConsoleError();

  // Periodic flush
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // v3: Session heartbeat every 60s
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

  // v3: Page performance tracking
  trackPagePerformance();

  // Track initial page view
  trackPageView(window.location.pathname);
}

/**
 * Track a page view. Call on route changes.
 */
function trackPageView(path) {
  trackEvent('page_view', {
    path,
    fullUrl: window.location.href,
    referrer: document.referrer || '',
    title: document.title || '',
  });
}

/**
 * Track an API error from axios interceptor — DEEP capture.
 * Pass the full axios error object for maximum detail.
 */
function trackApiError(error) {
  const config = error.config || {};
  const response = error.response;
  const request = error.request;

  // Calculate request duration if timing metadata exists
  const duration = config._startTime ? Date.now() - config._startTime : null;

  trackEvent('api_error', {
    // Request details
    url: config.url || '',
    baseURL: config.baseURL || '',
    fullUrl: (config.baseURL || '') + (config.url || ''),
    method: (config.method || '').toUpperCase(),
    requestHeaders: safeStringify(config.headers, 500),
    requestParams: safeStringify(config.params, 500),
    requestBody: safeStringify(config.data, 1000),

    // Response details
    status: response?.status || 0,
    statusText: response?.statusText || '',
    responseBody: safeStringify(response?.data, 1000),
    responseHeaders: safeStringify(response?.headers, 500),

    // Error details
    message: error.message || '',
    errorCode: error.code || '', // ECONNABORTED, ERR_NETWORK, ERR_CANCELED, etc.
    errorName: error.name || '',
    stack: truncate(error.stack, 1000),

    // Timing
    durationMs: duration,
    timeout: config.timeout || 0,

    // Network state
    online: navigator.onLine,

    // Was there a response at all? (distinguishes network error vs server error)
    hasResponse: !!response,
    hasRequest: !!request,
  }, 'error');
}

/**
 * Track a React ErrorBoundary catch.
 */
function trackReactError(error, errorInfo) {
  trackEvent('react_error', {
    message: error?.message || String(error),
    errorName: error?.name || '',
    stack: truncate(error?.stack, MAX_STRING),
    componentStack: truncate(errorInfo?.componentStack, 1000),
    currentUrl: window.location.href,
    documentTitle: document.title,
  }, 'error');
}

export {
  initTelemetry,
  trackEvent,
  trackPageView,
  trackApiError,
  trackApiSuccess,
  trackReactError,
  flush,
  SESSION_ID,
};
